import React, { useState, useRef, useContext, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  CalendarDays, 
  Users, 
  Plane, 
  Hotel, 
  MapPin, 
  Settings,
  Clock,
  Search,
  Sparkles,
  ChevronDown,
  Plus,
  Minus,
  Loader,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  Map,
  Globe,
  Star,
  TrendingUp,
  Compass,
  Mountain,
  Palette,
  Music,
  Camera,
  Coffee,
  Heart,
  Menu,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import AirportSearch from './AirportSearch';
import { AuthContext } from '../../../Context/AuthContext';
import FlightService from '../Discover/FlightCard/FlightService';
import DiscoverService, { useDiscoverService } from './DiscoverService';

function Discover() {
  const { frondId } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Discover');
  const [tripType, setTripType] = useState('roundTrip');
  const [passengers, setPassengers] = useState({
    adults: 1,
    children: 0,
    infants: 0
  });

  
  // Date state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [departureDate, setDepartureDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [tempDepartureDate, setTempDepartureDate] = useState(null);
  const [tempReturnDate, setTempReturnDate] = useState(null);
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [visibleMonths, setVisibleMonths] = useState(2); // Show 3 months by default
  
  // Project data state
  const [frondData, setFrondData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchingFlights, setSearchingFlights] = useState(false);
  
  // AI Travel Plan state
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [travelPlan, setTravelPlan] = useState(null);
  const [showTravelPlan, setShowTravelPlan] = useState(false);
  
  // Mobile state
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState('plan');

const [departureAirportCode, setDepartureAirportCode] = useState('');
const [arrivalAirportCode, setArrivalAirportCode] = useState('');
// Change from objects to strings
const [departureAirport, setDepartureAirport] = useState('');
const [arrivalAirport, setArrivalAirport] = useState('');


  const isMountedRef = useRef(true);


  // Helper function to extract 3-letter airport code
const extractAirportCode = (airportString) => {
  if (!airportString) return '';
  
  // If it's already a 3-letter code (uppercase letters)
  if (typeof airportString === 'string' && airportString.length === 3 && /^[A-Z]{3}$/.test(airportString)) {
    return airportString;
  }
  
  // Try to extract from format like "DUBAI (DXB) - INTERNATIONAL"
  const match = airportString.match(/\(([A-Z]{3})\)/);
  if (match && match[1]) {
    return match[1];
  }
  
  // Try to extract from format like "DXB - Dubai International Airport"
  const match2 = airportString.match(/^([A-Z]{3})\s*-\s*/);
  if (match2 && match2[1]) {
    return match2[1];
  }
  
  // If no pattern matches, return the first 3 uppercase letters or empty string
  const uppercaseLetters = airportString.replace(/[^A-Z]/g, '');
  return uppercaseLetters.substring(0, 3) || '';
};
  
  // Initialize flight service
  const flightService = FlightService;

  // Date formatting functions
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let date;

      // Handle DD/MM/YYYY format from API
      if (typeof dateString === 'string' && dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);
          date = new Date(year, month, day);
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    try {
      // Handle DD/MM/YYYY format from API
      if (typeof dateString === 'string' && dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);
          const date = new Date(year, month, day);
          return isNaN(date.getTime()) ? null : date;
        }
      }
      
      // Fallback to standard Date parsing
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };

  const formatForDisplay = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };


  useEffect(() => {
  // console.log('Departure Airport:', departureAirport);
  // console.log('Departure Code:', departureAirportCode);
  // console.log('Arrival Airport:', arrivalAirport);
  // console.log('Arrival Code:', arrivalAirportCode);
}, [departureAirport, arrivalAirport, departureAirportCode, arrivalAirportCode]);


  // Helper function to format airport for display
  const formatAirportForDisplay = (airport) => {
    if (!airport) return '';
    // If it already has a code in parentheses, return as is
    if (airport.match(/\([A-Z]{3}\)/)) {
      return airport;
    }
    return airport;
  };

  // Helper function to get the main airport for a city
  const getMainAirportForCity = (cityName, countryCode = null) => {
    return DiscoverService.getAirports({ cityName, countryCode })
      .then(airports => {
        if (!airports || airports.length === 0) {
          return null;
        }
        // Return the first airport as the main airport
        // It should be formatted as "City (CODE) - Airport Name"
        const main = airports[0];
        return `${main.city} (${main.code}) - ${main.name}`;
      })
      .catch(error => {
        console.error('Error fetching airports for city:', error);
        return null;
      });
  };

  // Load project data
  useEffect(() => {
    const loadProjectData = async () => {
      if (!frondId || !token) return;
      
      setIsLoading(true);
      try {
        const { data } = await axios.get(
          `/frond/${frondId}`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const projectData = data?.response?.data;
        setFrondData(projectData);
        
        // Update form fields with project data
        if (projectData?.frond?.start_date) {
          const startDate = parseDate(projectData.frond.start_date);
          setDepartureDate(startDate);
          setTempDepartureDate(startDate);
        }
        if (projectData?.frond?.end_date) {
          const endDate = parseDate(projectData.frond.end_date);
          setReturnDate(endDate);
          setTempReturnDate(endDate);
        }
        
        // Set arrival airport based on frond city - fetch the main airport
        if (projectData?.frond?.city) {
          const countryCode = projectData?.frond?.country_code || projectData?.frond?.country || null;
          
          // Fetch airports for the city and get the main one
          const airports = await DiscoverService.getAirports({ 
            cityName: projectData.frond.city, 
            countryCode 
          });
          
          if (airports && airports.length > 0) {
            // Get the first airport (main airport for the city)
            const mainAirport = airports[0];
            const airportString = `${mainAirport.city} (${mainAirport.code}) - ${mainAirport.name}`;
            setArrivalAirport(airportString);
            setArrivalAirportCode(mainAirport.code);
          } else {
            // Fallback to city name if no airports found
            setArrivalAirport(projectData.frond.city);
          }
        }
        
      } catch (error) {
        console.error('Error fetching project data:', error);
        toast.error(error?.response?.data?.message || 'Failed to load project data');
      }
      setIsLoading(false);
    };
    
    loadProjectData();
  }, [frondId, token]);

  // Update dates in backend
const updateDates = async () => {
  if (!tempDepartureDate || !tempReturnDate) {
    toast.error('Please select both start and end dates');
    return;
  }

  if (tempDepartureDate >= tempReturnDate) {
    toast.error('End date must be after start date');
    return;
  }

  setIsSaving(true);
  try {
    // Format dates as DD/MM/YYYY (matching EditDetailsModal.jsx format)
    const formatDateForAPI = (date) => {
      if (!date) return null;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const formattedStartDate = formatDateForAPI(tempDepartureDate);
    const formattedEndDate = formatDateForAPI(tempReturnDate);

    // Build form data matching the EditDetailsModal.jsx structure
    const formData = {
      title: frondData?.frond?.title || 'Untitled Trip',
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      category: frondData?.frond?.category || 'travel',
      desc: frondData?.frond?.desc || '',
      budget: frondData?.frond?.budget || '',
      currency: frondData?.frond?.currency || '',
      priority: frondData?.frond?.priority || null,
      latitude: frondData?.frond?.latitude || null,
      longitude: frondData?.frond?.longitude || null,
      location: frondData?.frond?.location || '',
      country: frondData?.frond?.country || '',
      city: frondData?.frond?.city || '',
    };

    // Use POST with _method=PUT (matching EditDetailsModal.jsx)
    await axios.post(
      `/frond-about/${frondId}?_method=PUT`,
      formData,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      }
    );

    // ... rest of your success handling code
    setDepartureDate(tempDepartureDate);
    setReturnDate(tempReturnDate);
    
    setFrondData(prev => ({
      ...prev,
      frond: {
        ...prev?.frond,
        start_date: formattedStartDate,
        end_date: formattedEndDate
      }
    }));
    
    setShowDatePicker(false);
    toast.success('Dates updated successfully!');
  } catch (error) {
    console.error('Error updating dates:', error);
    // Log detailed error response
    if (error.response?.data) {
      console.error('Error details:', JSON.stringify(error.response.data, null, 2));
    }
    toast.error(error?.response?.data?.message || error?.message || 'Failed to update dates');
  }
  setIsSaving(false);
};

const handleSearchFlights = async () => {
  // Validate required fields
  if (!departureAirportCode || departureAirportCode.length !== 3) {
    toast.error('Please select a valid departure airport');
    return;
  }

  if (!arrivalAirportCode || arrivalAirportCode.length !== 3) {
    toast.error('Please select a valid arrival airport');
    return;
  }

  if (!departureDate) {
    toast.error('Please select departure date');
    return;
  }

  if (tripType === 'roundTrip' && !returnDate) {
    toast.error('Please select return date for round trip');
    return;
  }

  setSearchingFlights(true);

  try {
    
    const formatDateForAPI = (date) => {
      if (!date) return null;
      // Use local timezone to prevent UTC conversion issues
      // toISOString() converts to UTC which can shift the date by one day
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const searchParams = {
      departureCode: departureAirportCode, 
      destinationCode: arrivalAirportCode,  
      departureDate: formatDateForAPI(departureDate),
      returnDate: tripType === 'roundTrip' ? formatDateForAPI(returnDate) : null,
      adults: passengers.adults,
      children: passengers.children,
      infants: passengers.infants,
      tripType: tripType,
      currency: 'USD'
    };

    // console.log('Flight search params:', searchParams);

 
    navigate(`flight-list`, {
      state: {
        searchParams,
        from: departureAirport,
        to: arrivalAirport
      }
    });

  } catch (error) {
    console.error('Error preparing flight search:', error);
    toast.error('Error preparing flight search');
  } finally {
    setSearchingFlights(false);
  }
};

  // Handle search hotels
  const handleSearchHotels = async () => {
    if (!arrivalAirport) {
      toast.error('Please select a destination');
      return;
    }

    if (!departureDate || !returnDate) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setSearchingFlights(true);

    try {
      const formatDateForAPI = (date) => {
        if (!date) return null;
        // Use local timezone to prevent UTC conversion issues
        // toISOString() converts to UTC which can shift the date by one day
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const hotelSearchParams = {
        location: arrivalAirport,
        checkInDate: formatDateForAPI(departureDate),
        checkOutDate: formatDateForAPI(returnDate),
        adults: passengers.adults,
        children: passengers.children,
        infants: passengers.infants,
        rooms: 1
      };

      // console.log('Hotel search params:', hotelSearchParams);

      // Navigate to hotel list page with search parameters
      navigate(`hotel-list`, {
        state: {
          searchParams: hotelSearchParams,
          location: arrivalAirport
        }
      });

    } catch (error) {
      console.error('Error preparing hotel search:', error);
      toast.error('Error preparing hotel search');
    } finally {
      setSearchingFlights(false);
    }
  };

  // Handle generate AI travel plan
  const handleGenerateTravelPlan = async () => {
    if (!frondId || !token) {
      toast.error('Please log in to generate a travel plan');
      return;
    }

    setGeneratingPlan(true);
    setShowTravelPlan(true);

    try {
      // Use axios with current token from AuthContext
      const response = await axios.get(
        `/discover/${frondId}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.data) {
        setTravelPlan(response.data);
        toast.success('AI Travel Plan generated successfully!');
      } else {
        throw new Error('No data returned from server');
      }
    } catch (error) {
      console.error('Error generating travel plan:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to generate travel plan');
      setTravelPlan(null);
    } finally {
      setGeneratingPlan(false);
    }
  };

  // Helper function to strip HTML tags from text
  const stripHtmlTags = (html) => {
    if (!html || typeof html !== 'string') return '';
    return html
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/</g, '<') // Replace < with <
      .replace(/>/g, '>') // Replace > with >
      .replace(/"/g, '"') // Replace " with "
      .replace(/&#39;/g, "'") // Replace &#39; with '
      .replace(/<br\s*\/?>/gi, '\n') // Replace <br> with newlines
      .replace(/<li>/gi, '\n• ') // Replace <li> with bullet points
      .replace(/<\/li>/gi, '') // Remove </li>
      .replace(/\n\s*\n/g, '\n\n') // Clean up multiple newlines
      .trim();
  };

  // Helper function to format plan content for download
  const formatPlanForDownload = (content, title) => {
    if (!content) return '';
    
    let text = '';
    text += '═══════════════════════════════════════════════════════════════\n';
    text += `                    ${title.toUpperCase()}\n`;
    text += '═══════════════════════════════════════════════════════════════\n\n';
    
    if (typeof content === 'string') {
      text += stripHtmlTags(content);
    } else {
      Object.entries(content).forEach(([sectionTitle, sectionContent]) => {
        text += '───────────────────────────────────────────────────────────────\n';
        text += `${stripHtmlTags(sectionTitle).toUpperCase()}\n`;
        text += '───────────────────────────────────────────────────────────────\n\n';
        
        if (typeof sectionContent === 'string') {
          text += stripHtmlTags(sectionContent) + '\n\n';
        } else if (typeof sectionContent === 'object') {
          Object.entries(sectionContent).forEach(([subsectionTitle, subsectionContent]) => {
            text += `• ${stripHtmlTags(subsectionTitle)}: ${stripHtmlTags(String(subsectionContent))}\n`;
          });
          text += '\n';
        }
      });
    }
    
    text += '═══════════════════════════════════════════════════════════════\n';
    text += `Generated for: ${frondData?.frond?.city || 'Your Trip'}\n`;
    text += `Duration: ${frondData?.frond?.start_date && frondData?.frond?.end_date 
      ? `${formatDate(frondData.frond.start_date)} - ${formatDate(frondData.frond.end_date)}`
      : 'Not specified'}\n`;
    text += '═══════════════════════════════════════════════════════════════\n';
    
    return text;
  };

  // Helper function to download content as a file
  const downloadContent = (content, filename) => {
    try {
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Plan downloaded successfully!');
    } catch (error) {
      console.error('Error downloading plan:', error);
      toast.error('Failed to download plan');
    }
  };
  
  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay, year, month };
  };

  // Helper function to normalize date to midnight for accurate comparison
  const normalizeDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Check if a date is within the selected range
  const isDateInRange = (date) => {
    const normalizedDate = normalizeDate(date);
    const start = normalizeDate(tempDepartureDate);
    const end = normalizeDate(tempReturnDate);
    
    if (start && end) {
      return normalizedDate >= start && normalizedDate <= end;
    } else if (start) {
      return normalizedDate.getTime() === start.getTime();
    }
    return false;
  };

  const handleDayClick = (day, monthDate) => {
    const selectedDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    
    // Normalize dates to midnight for comparison
    const normalizedSelected = normalizeDate(selectedDate);
    const normalizedStart = normalizeDate(tempDepartureDate);
    const normalizedEnd = normalizeDate(tempReturnDate);
    
    if (!normalizedStart || (normalizedStart && normalizedEnd)) {
      // Start new selection
      setTempDepartureDate(selectedDate);
      setTempReturnDate(null);
    } else if (normalizedStart && !normalizedEnd) {
      // Complete the range
      if (normalizedSelected > normalizedStart) {
        setTempReturnDate(selectedDate);
      } else if (normalizedSelected.getTime() === normalizedStart.getTime()) {
        // Clicked same day, keep single date selection
        setTempReturnDate(null);
      } else {
        // Selected date is before start date, swap
        setTempReturnDate(tempDepartureDate);
        setTempDepartureDate(selectedDate);
      }
    }
  };

  const isDateSelected = (day, monthDate) => {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const normalizedDate = normalizeDate(date);
    const normalizedDeparture = normalizeDate(tempDepartureDate);
    const normalizedReturn = normalizeDate(tempReturnDate);
    
    if (normalizedDeparture && normalizedDate.getTime() === normalizedDeparture.getTime()) return true;
    if (normalizedReturn && normalizedDate.getTime() === normalizedReturn.getTime()) return true;
    return isDateInRange(date);
  };

  const isStartDate = (day, monthDate) => {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const normalizedDate = normalizeDate(date);
    const normalizedDeparture = normalizeDate(tempDepartureDate);
    return normalizedDeparture && normalizedDate.getTime() === normalizedDeparture.getTime();
  };

  const isEndDate = (day, monthDate) => {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const normalizedDate = normalizeDate(date);
    const normalizedReturn = normalizeDate(tempReturnDate);
    return normalizedReturn && normalizedDate.getTime() === normalizedReturn.getTime();
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(direction === 'next' ? newDate.getMonth() + 1 : newDate.getMonth() - 1);
      return newDate;
    });
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getNextMonths = () => {
    const months = [];
    for (let i = 0; i < visibleMonths; i++) {
      const next = new Date(currentMonth);
      next.setMonth(next.getMonth() + i);
      months.push(next);
    }
    return months;
  };

  const openDatePicker = () => {
    setTempDepartureDate(departureDate);
    setTempReturnDate(returnDate);
    setShowDatePicker(true);
  };

  const closeDatePicker = () => setShowDatePicker(false);
  const clearDates = () => {
    setTempDepartureDate(null);
    setTempReturnDate(null);
  };

  const handlePassengerChange = (type, delta) => {
    setPassengers(prev => ({
      ...prev,
      [type]: Math.max(0, prev[type] + delta)
    }));
  };

  const totalPassengers = passengers.adults + passengers.children + passengers.infants;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 p-4 md:p-8 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <Compass className="absolute inset-0 m-auto text-blue-400 animate-pulse" size={24} />
          </div>
          <span className="text-lg text-white font-medium">Discovering your journey...</span>
        </div>
      </div>
    );
  }

  const getDestination = () => {
    if (frondData?.frond?.city) return frondData.frond.city;
    if (frondData?.frond?.location) return frondData.frond.location;
    return 'destination';
  };

  // Render calendar for a specific month
  const renderCalendar = (monthDate, isSecondMonth = false, index = 0) => {
    const { daysInMonth, startingDay } = getDaysInMonth(monthDate);
    const days = [];
    
    // Create a unique prefix for this month
    const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth()}`;
    
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${monthKey}-${i}`} className="h-10"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = isDateSelected(day, monthDate);
      const isStart = isStartDate(day, monthDate);
      const isEnd = isEndDate(day, monthDate);
      
      // Calculate if this date is disabled (before today)
      const dateObj = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
      const today = normalizeDate(new Date());
      const isDisabled = today && normalizeDate(dateObj) < today;
      
      days.push(
        <button
          key={`${monthKey}-${day}`}
          onClick={() => !isDisabled && handleDayClick(day, monthDate)}
          disabled={isDisabled}
          className={`
            h-8 w-8 md:h-10 md:w-10 rounded-lg flex items-center justify-center text-xs md:text-sm font-medium
            transition-all duration-300 transform hover:scale-105
            ${isDisabled ? 'text-gray-300 cursor-not-allowed' : ''}
            ${isSelected && !isDisabled ? 'bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700' : ''}
            ${isStart || isEnd ? 'bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-lg' : ''}
            ${!isStart && !isEnd && !isDisabled ? 'hover:bg-blue-100' : ''}
          `}
        >
          {day}
        </button>
      );
    }
    
    return (
      <div className={`${index > 0 ? 'md:ml-8 mt-6 md:mt-0' : ''}`}>
        <div className="text-center mb-4">
          <h3 className="font-semibold text-gray-900 text-base md:text-lg">{getMonthName(monthDate)}</h3>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-3">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dayName, index) => (
            <div 
              key={`day-header-${monthKey}-${index}`}
              className="text-center text-xs md:text-sm text-gray-500 font-semibold"
            >
              {dayName}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    );
  };

  // Mobile Navigation Menu
  const MobileNav = () => (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-40">
      <div className="flex">
        {[
          { id: 'plan', label: 'Plan', icon: Compass },
          { id: 'travelers', label: 'Travelers', icon: Users },
          { id: 'interests', label: 'Interests', icon: Heart },
          { id: 'actions', label: 'Actions', icon: Sparkles }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => {
              setActiveMobileSection(item.id);
              document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`flex-1 flex flex-col items-center py-3 ${
              activeMobileSection === item.id 
                ? 'text-blue-600 bg-blue-50' 
                : 'text-gray-600'
            }`}
          >
            <item.icon size={20} />
            <span className="text-xs mt-1">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pb-20 md:pb-0">
      {/* Mobile Header */}
      <div className="md:hidden  bg-gradient-to-br from-orange-500 to-pink-500 text-white sticky top-0 z-30">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-full"
            >
              <ChevronLeft size={24} />
            </button>
            <div className="text-center">
              <h1 className="text-lg font-bold line-clamp-1">
                {frondData?.frond?.title || 'Discover'}
              </h1>
              <p className="text-xs text-blue-200">
                {frondData?.frond?.city || 'Select destination'}
              </p>
            </div>
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 hover:bg-white/10 rounded-full"
            >
              <Menu size={24} />
            </button>
          </div>
          
          {/* {showMobileMenu && (
            <div className="mt-3 bg-black/20 backdrop-blur-sm rounded-xl p-2">
              <div className="grid grid-cols-2 gap-2">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.name}
                      onClick={() => setActiveTab(tab.name)}
                      className={`flex items-center gap-2 py-2 px-3 rounded-lg text-sm font-medium ${
                        activeTab === tab.name
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                          : 'text-blue-100 hover:bg-white/10'
                      }`}
                    >
                      <Icon size={16} />
                      {tab.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )} */}
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-gradient-to-r mb-6 from-gray-900 to-blue-900 text-white rounded-3xl">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-500/20 rounded-3xl">
                  <Compass size={24} className="text-blue-300" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  {frondData?.frond?.title || 'Discover Your Journey'}
                </h1>
                 <div className="flex flex-wrap items-center gap-2 md:gap-4 text-blue-200">
                <div className="flex items-center gap-2">
                  <MapPin size={14} />
                  <span className="text-sm">
                    {frondData?.frond?.city && frondData?.frond?.country 
                      ? `${frondData.frond.city}, ${frondData.frond.country}`
                      : 'Select destination'
                    }
                  </span>
                </div>
                {frondData?.frond?.budget && (
                  <>
                    <span className="hidden md:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Star size={14} />
                      <span className="text-sm">Budget: {frondData?.frond?.currency || ''} {frondData.frond.budget}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
              </div>
             
            <div>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
            <div className="bg-black/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-blue-500/30 rounded-lg">
                  <CalendarDays size={16} className="md:size-[20px]" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-blue-200">Duration</p>
                  
                </div>
                
              </div>
              <p className="text-sm md:text-base font-semibold mt-4 flex justify-center items-center">
                    {frondData?.frond?.start_date && frondData?.frond?.end_date 
                      ? `${formatDate(frondData.frond.start_date)}  .  ${formatDate(frondData.frond.end_date)}`
                      : 'Select dates'
                    }
                  </p>
            </div>
            <div className="bg-black/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-green-500/30 rounded-lg">
                  <Users size={16} className="md:size-[20px]" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-blue-200">Travelers</p>
                  
                </div>
              </div>
              <p className="text-sm md:text-base font-semibold  mt-4 flex justify-center items-center">
                    {totalPassengers} {totalPassengers === 1 ? 'Person' : 'People'}</p>
            </div>
            <div className="bg-black/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-purple-500/30 rounded-lg">
                  <Sparkles size={16} className="md:size-[20px]" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-blue-200">Trip Type</p>
                  
                </div>
              </div>
              <p className="text-sm md:text-base font-semibold mt-4 flex justify-center items-center">{tripType}</p>
            </div>
            <div className="bg-black/20 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-amber-500/30 rounded-lg">
                  <Plane size={16} className="md:size-[20px]" />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-blue-200">Destination</p>
                 
                </div>
              </div>
               <p className="text-sm md:text-base font-semibold mt-4 flex justify-center items-center">{arrivalAirport}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-4 md:py-8 -mt-4 md:-mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
          {/* Left Column - Plan Section */}
          <div className="lg:col-span-2 space-y-4 md:space-y-8">
            {/* Trip Configuration Card */}
            <div id="plan" className="bg-white rounded-2xl md:rounded-3xl shadow-lg md:shadow-2xl overflow-hidden border border-gray-100">
              <div className="bg-mainGradiant p-4 md:p-6">
                <h2 className="text-lg md:text-2xl font-bold text-white flex items-center gap-2 md:gap-3">
                  <Compass size={20} className="md:size-6" />
                  Plan Your Journey
                </h2>
                <p className="text-blue-100 mt-1 text-xs md:text-sm">Customize every detail of your trip</p>
              </div>
              <div className="p-4 md:p-6 lg:p-8">
                {/* Trip Type Toggle */}
                <div className="mb-4 md:mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3">Trip Type</label>
                  <div className="flex gap-2 bg-gradient-to-r from-gray-100 to-blue-50 p-1 rounded-xl md:rounded-2xl max-w-sm">
                    {[
                      { display: 'Round Trip', value: 'roundTrip' },
                      { display: 'One Way', value: 'oneWay' }
                    ].map(type => (
                      <button
                        key={type.value}
                        onClick={() => setTripType(type.value)}
                        className={`flex-1 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-semibold transition-all transform hover:scale-[1.02] ${
                          tripType === type.value
                            ? 'bg-mainGradiant text-white shadow-lg'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {type.display}
                      </button>
                    ))}
                  </div>
                </div>  


                {/* Flight Details */}
                <div className="space-y-4 md:space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3 flex items-center gap-2">
                      <Plane size={14} className="md:size-4" />
                      <span className="text-xs md:text-sm">Departure From</span>
                    </label>
                    <AirportSearch
                      value={departureAirport}
                      onChange={(value) => {
                        setDepartureAirport(value);
                        const code = extractAirportCode(value);
                        setDepartureAirportCode(code);
                      }}
                      placeholder="Search departure airport..."
                      showToast={toast}
                      token={token}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3 flex items-center gap-2">
                      <Plane size={14} className="md:size-4" />
                      <span className="text-xs md:text-sm">Arrival To</span>
                    </label>
                    <AirportSearch
                    value={arrivalAirport}
                    onChange={(value) => {
                      setArrivalAirport(value);
                      // Extract code from the value
                      const code = extractAirportCode(value);
                      setArrivalAirportCode(code);
                    }}
                    placeholder={frondData?.frond?.city ? 
                      `Search for ${frondData.frond.city} or other cities...` : 
                      "Search arrival airport..."
                    }
                    showToast={toast}
                    label="destination city"
                    token={token}
                    />
                  </div>
                </div>

                  {/* Date Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3 flex items-center gap-2">
                      <CalendarDays size={14} className="md:size-4" />
                      <span className="text-xs md:text-sm">Travel Dates</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div className="group">
                        <div className="text-xs md:text-sm text-gray-500 mb-2">Departure Date</div>
                        <button
                          onClick={openDatePicker}
                          className="w-full p-3 md:p-4 bg-gradient-to-r from-gray-50 to-blue-50 border-2 border-gray-200 rounded-xl md:rounded-2xl text-left focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all hover:border-blue-300 group-hover:shadow-md"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-sm md:text-lg font-medium ${departureDate ? "text-gray-900" : "text-gray-400"}`}>
                                {departureDate ? formatForDisplay(departureDate) : "Select date"}
                              </span>
                              <div className="text-xs md:text-sm text-gray-500 mt-1">
                                {departureDate ? formatDate(departureDate.toISOString()) : 'dd/mm/yyyy'}
                              </div>
                            </div>
                            <Calendar size={20} className="text-blue-500 group-hover:scale-110 transition-transform" />
                          </div>
                        </button>
                      </div>

                      <div className="group">
                        <div className="text-xs md:text-sm text-gray-500 mb-2">Return Date</div>
                        <button
                          onClick={openDatePicker}
                          className="w-full p-3 md:p-4 bg-gradient-to-r from-gray-50 to-blue-50 border-2 border-gray-200 rounded-xl md:rounded-2xl text-left focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all hover:border-blue-300 group-hover:shadow-md"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-sm md:text-lg font-medium ${returnDate ? "text-gray-900" : "text-gray-400"}`}>
                                {returnDate ? formatForDisplay(returnDate) : "Select date"}
                              </span>
                              <div className="text-xs md:text-sm text-gray-500 mt-1">
                                {returnDate ? formatDate(returnDate.toISOString()) : 'dd/mm/yyyy'}
                              </div>
                            </div>
                            <Calendar size={20} className="text-blue-500 group-hover:scale-110 transition-transform" />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interests Card */}
            <div id="interests" className="bg-white rounded-2xl md:rounded-3xl shadow-lg md:shadow-2xl p-4 md:p-6 lg:p-8 border border-gray-100">
              <button 
                onClick={() => navigate(`travel-interests`)} 
                className="w-full p-3 md:p-4 bg-gradient-to-r from-white to-blue-50 border-2 border-blue-600 text-blue-600 rounded-xl md:rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-50 transition-all transform hover:scale-[1.02] shadow-sm text-sm md:text-base"
              >
                <Heart size={16} className="md:size-5" />
                Select Travel Interests
              </button>
            </div>
          </div>

          {/* Right Column - Travelers & Actions */}
          <div className="space-y-2  md:space-y-8">
{/* Passengers Card */}
<div id="travelers" className="bg-white rounded-2xl md:rounded-3xl shadow-lg md:shadow-2xl p-2 md:p-6 lg:p-2 border border-gray-100">
  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
    <Users size={20} className="md:size-6 text-orange-500" />
    Travelers
  </h3>
  
  <div className="space-y-3 md:space-y-4">
    {[
      { type: 'adults', label: 'Adults', description: '12+ years' },
      { type: 'children', label: 'Children', description: '2-11 years' },
      { type: 'infants', label: 'Infants', description: 'Under 2 years' }
    ].map(item => (
      <div key={item.type} className="flex items-center justify-between p-3 md:p-4 bg-gradient-to-r from-gray-50 to-orange-50 rounded-xl md:rounded-2xl border border-gray-200 hover:bg-orange-50 transition-colors">
        {/* Left side - Labels with responsive width */}
        <div className="flex-1 min-w-0 pr-2">
          <p className="font-bold text-gray-900 text-sm md:text-base lg:text-md truncate">
            {item.label}
          </p>
          <p className="text-xs md:text-sm  text-gray-600 mt-0.5 truncate">
            {item.description}
          </p>
        </div>
        
        {/* Right side - Counter buttons - fixed width */}
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <button
            onClick={() => handlePassengerChange(item.type, -1)}
            disabled={passengers[item.type] <= 0}
            className="w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 flex items-center justify-center bg-white rounded-full border-2 border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-all hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-sm active:scale-95"
            aria-label={`Decrease ${item.label}`}
          >
            <Minus size={14} className="md:size-4 lg:size-5 text-gray-700" />
          </button>
          
          <div className="w-8 md:w-10 text-center">
            <span className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900">
              {passengers[item.type]}
            </span>
          </div>
          
          <button
            onClick={() => handlePassengerChange(item.type, 1)}
            className="w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 flex items-center justify-center bg-white rounded-full border-2 border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-all hover:scale-110 shadow-sm active:scale-95"
            aria-label={`Increase ${item.label}`}
          >
            <Plus size={14} className="md:size-4 lg:size-5 text-gray-700" />
          </button>
        </div>
      </div>
    ))}
  </div>

  {/* Total Travelers */}
  <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-200">
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-xs md:text-sm text-gray-500 mb-1">Total Travelers</p>
        <p className="text-xl md:text-2xl font-bold text-gray-900 truncate">
          {totalPassengers} {totalPassengers === 1 ? 'Person' : 'People'}
        </p>
      </div>
      <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex-shrink-0 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl md:rounded-2xl shadow-md">
        <Users size={16} className="md:size-5 lg:size-7 text-white" />
      </div>
    </div>
  </div>
</div>

            {/* Action Buttons */}
            <div id="actions" className="space-y-3 md:space-y-4">
              <button 
                onClick={handleGenerateTravelPlan}
                disabled={generatingPlan}
                className="w-full p-3 md:p-4 lg:p-5 bg-mainGradiant text-white rounded-xl md:rounded-2xl font-bold flex items-center justify-center gap-2 md:gap-2 hover:opacity-90 transition-all transform hover:scale-[1.02] shadow-lg md:shadow-xl text-sm md:text-base disabled:opacity-70"
              >
                {generatingPlan ? (
                  <>
                    <Loader size={16} className="md:size-6 animate-spin" />
                    <span>Generating Plan...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={16} className="md:size-6" />
                    <span>Generate AI Travel Plan</span>
                  </>
                )}
              </button>
              
              <div className="grid grid-cols-2 lg:gap-1 gap-2 md:gap-4">
                <button className="p-2 md:p-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl md:rounded-2xl font-semibold flex flex-col items-center justify-center gap-2 hover:opacity-90 transition-all transform hover:scale-[1.02]"
                  onClick={handleSearchFlights}
                disabled={searchingFlights || !departureAirport || !arrivalAirport || !departureDate || (tripType === 'roundTrip' && !returnDate)}>
                  <Plane size={16} className="md:size-6" />
                  <span className="text-xs md:text-sm">Flights</span>
                </button>
                
                <button className="p-3 md:p-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl md:rounded-2xl font-semibold flex flex-col items-center justify-center gap-2 hover:opacity-90 transition-all transform hover:scale-[1.02]"
                  onClick={handleSearchHotels}>
                  <Hotel size={16} className="md:size-6" />
                  <span className="text-xs md:text-sm">Hotels</span>
                </button>
                
<button 
                  onClick={() => navigate(`activity-list`)}
                  className="p-3 md:p-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl md:rounded-2xl font-semibold flex flex-col items-center justify-center gap-2 hover:opacity-90 transition-all transform hover:scale-[1.02]"
                >
                  <MapPin size={16} className="md:size-6" />
                  <span className="text-xs md:text-sm">Activities</span>
                </button>
                
<button 
                  onClick={() => navigate('explore-places')}
                  className="p-3 md:p-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl md:rounded-2xl font-semibold flex flex-col items-center justify-center gap-2 hover:opacity-90 transition-all transform hover:scale-[1.02]"
                >
                  <Search size={16} className="md:size-6" />
                  <span className="text-xs md:text-sm">Explore</span>
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            {/* <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl md:rounded-3xl p-4 md:p-6 border border-blue-100">
              <h4 className="font-bold text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
                <TrendingUp size={16} className="md:size-5 text-blue-500" />
                <span className="text-sm md:text-base">Trip Overview</span>
              </h4>
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 text-xs md:text-sm">Trip Duration</span>
                  <span className="font-semibold text-xs md:text-sm">
                    {frondData?.frond?.start_date && frondData?.frond?.end_date ? '10 days' : 'Not set'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 text-xs md:text-sm">Budget</span>
                  <span className="font-semibold text-xs md:text-sm">
                    {frondData?.frond?.currency || ''} {frondData?.frond?.budget || 'Not set'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 text-xs md:text-sm">Destination</span>
                  <span className="font-semibold text-xs md:text-sm">{getDestination()}</span>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <MobileNav />

      {/* Date Picker Modal - Enhanced */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-3 md:p-4 pt-20 md:pt-24 overflow-y-auto">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl max-w-md md:max-w-2xl w-full overflow-hidden border border-gray-200 my-4 md:my-8 mx-auto">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-4 md:p-6 relative">
              <div className="flex justify-between items-center">
                <h2 className="text-lg md:text-2xl font-bold text-white">Select Travel Dates</h2>
                <button
                  onClick={closeDatePicker}
                  className="p-1.5 md:p-2 hover:bg-white/20 rounded-full transition-colors bg-white/10 backdrop-blur-sm"
                >
                  <X size={20} className="md:size-6 text-white" />
                </button>
              </div>
              <p className="text-blue-100 mt-1 text-xs md:text-sm">Choose your departure and return dates</p>
            </div>
            
            <div className="p-4 md:p-6">
              {/* Date Display */}
              <div className="mb-4 md:mb-8">
                <div className="text-xs md:text-sm text-gray-500 mb-2 md:mb-3 font-medium">Selected Dates</div>
                <div className="flex items-center gap-2 md:gap-4">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">Start Date</div>
                    <div className="p-3 md:p-4 bg-gradient-to-r from-gray-50 to-blue-50 border-2 border-gray-200 rounded-lg md:rounded-xl font-semibold text-sm md:text-base">
                      {tempDepartureDate ? formatForDisplay(tempDepartureDate) : 'dd/mm/yyyy'}
                    </div>
                  </div>
                  <div className="text-xl md:text-2xl text-gray-300">→</div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">End Date</div>
                    <div className="p-3 md:p-4 bg-gradient-to-r from-gray-50 to-blue-50 border-2 border-gray-200 rounded-lg md:rounded-xl font-semibold text-sm md:text-base">
                      {tempReturnDate ? formatForDisplay(tempReturnDate) : 'dd/mm/yyyy'}
                    </div>
                  </div>
                  <button
                    onClick={clearDates}
                    className="p-2 md:p-3 hover:bg-gray-100 rounded-lg md:rounded-xl transition-colors self-end"
                  >
                    <X size={16} className="md:size-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              {/* Calendar Navigation */}
              <div className="flex justify-between items-center mb-4 md:mb-6 px-2 md:px-4">
                <button
                  onClick={() => navigateMonth('prev')}
                  className="p-2 md:p-3 hover:bg-gray-100 rounded-lg md:rounded-xl transition-colors"
                >
                  <ChevronLeft size={16} className="md:size-5" />
                </button>
                <div className="flex gap-2 md:gap-4 flex-wrap justify-center">
                  {getNextMonths().slice(0, 2).map((month, index) => (
                    <div key={index} className="font-bold text-sm md:text-lg text-gray-900 px-2">
                      {getMonthName(month)}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigateMonth('next')}
                  className="p-2 md:p-3 hover:bg-gray-100 rounded-lg md:rounded-xl transition-colors"
                >
                  <ChevronRight size={16} className="md:size-5" />
                </button>
              </div>
              
              {/* Multi-Month Calendar */}
              <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-4 md:mb-8">
                {getNextMonths().slice(0, 3).map((month, index) => (
                  <div key={index} className="min-w-[280px] md:min-w-[300px]">
                    {renderCalendar(month, false, index)}
                  </div>
                ))}
              </div>
              
              {/* Save Button */}
              <button
                onClick={updateDates}
                disabled={isSaving || !tempDepartureDate || !tempReturnDate}
                className="w-full py-3 md:py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg md:rounded-xl font-bold text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isSaving ? (
                  <>
                    <Loader size={16} className="md:size-5 animate-spin" />
                    <span>Saving Dates...</span>
                  </>
                ) : (
                  <>
                    <CalendarDays size={16} className="md:size-5" />
                    <span>Confirm Dates</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Travel Plan Modal */}
      {showTravelPlan && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 md:p-4">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-gray-200 flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-400 to-pink-500 p-4 md:p-6 flex-shrink-0">
              <div className="flex justify-between items-center">
                <h2 className="text-lg md:text-2xl font-bold text-white flex items-center gap-2">
                  <FileText size={20} className="md:size-6" />
                  AI Travel Plan
                </h2>
                <button
                  onClick={() => setShowTravelPlan(false)}
                  className="p-1.5 md:p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X size={20} className="md:size-6 text-white" />
                </button>
              </div>
              <p className="text-purple-100 mt-1 text-xs md:text-sm">
                Your personalized travel itinerary for {frondData?.frond?.city || 'your destination'}
              </p>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 md:p-6 overflow-y-auto flex-1">
              {generatingPlan ? (
                // Loading State
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
                    <Sparkles className="absolute inset-0 m-auto text-purple-500 animate-pulse" size={24} />
                  </div>
                  <p className="mt-4 text-lg text-gray-600">Generating your personalized travel plan...</p>
                  <p className="text-sm text-gray-400 mt-2">This may take a few moments</p>
                </div>
              ) : travelPlan ? (
                // Success State - Display Plan and Deals
                <div className="space-y-6">
                  {/* Plan Section - Handle both string and object responses */}
                  {travelPlan.plan && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 md:p-6 border border-purple-100">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <CheckCircle size={20} className="text-purple-500" />
                          Your Travel Plan
                        </h3>
                        <button
                          onClick={() => {
                            const content = formatPlanForDownload(travelPlan.plan, 'Travel Plan');
                            downloadContent(content, `travel-plan-${frondData?.frond?.city || 'trip'}.txt`);
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-orange-400 to-pink-500 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                        >
                          <FileText size={14} />
                          <span>Download</span>
                        </button>
                      </div>
                      <div className="prose prose-sm max-w-none">
                        {typeof travelPlan.plan === 'string' ? (
                          <p className="text-gray-700 whitespace-pre-wrap">{stripHtmlTags(travelPlan.plan)}</p>
                        ) : (
                          // Handle object response with nested sections
                          <div className="space-y-4">
                            {Object.entries(travelPlan.plan).map(([sectionTitle, sectionContent], index) => (
                              <div key={index} className="mb-4">
                                <h4 className="font-semibold text-gray-800 text-base mb-2">{stripHtmlTags(sectionTitle)}</h4>
                                {typeof sectionContent === 'string' ? (
                                  <p className="text-gray-700 whitespace-pre-wrap">{stripHtmlTags(sectionContent)}</p>
                                ) : (
                                  <div className="pl-4 border-l-2 border-purple-200">
                                    {Object.entries(sectionContent).map(([subsectionTitle, subsectionContent], subIndex) => (
                                      <div key={subIndex} className="mb-3">
                                        <h5 className="font-medium text-gray-700 text-sm">{stripHtmlTags(subsectionTitle)}</h5>
                                        <p className="text-gray-600 text-sm">{stripHtmlTags(String(subsectionContent))}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                 

                  {/* Project Info */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-2">Trip Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Destination:</span>
                        <p className="font-medium text-gray-900">
                          {frondData?.frond?.city && frondData?.frond?.country 
                            ? `${frondData.frond.city}, ${frondData.frond.country}`
                            : 'Not specified'
                          }
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Duration:</span>
                        <p className="font-medium text-gray-900">
                          {frondData?.frond?.start_date && frondData?.frond?.end_date 
                            ? `${formatDate(frondData.frond.start_date)} - ${formatDate(frondData.frond.end_date)}`
                            : 'Not specified'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Error State
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle size={32} className="text-red-500" />
                  </div>
                  <p className="mt-4 text-lg text-gray-600">Failed to generate travel plan</p>
                  <p className="text-sm text-gray-400 mt-2">Please try again later</p>
                  <button
                    onClick={() => setShowTravelPlan(false)}
                    className="mt-4 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            {travelPlan && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  onClick={() => setShowTravelPlan(false)}
                  className="w-full py-3 bg-gradient-to-r from-orange-400 to-pink-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-lg"
                >
                  <CheckCircle size={18} />
                  <span>Looks Great!</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Render child routes */}
      <Outlet />
    </div>
  );
}

export default Discover;